import React, { createContext, TouchEventHandler, useContext } from "react"
import { Box, Flex } from "@chakra-ui/react"
import { BarPlayer } from "./videoPlayer/controls-player/bar-player"
import {
  PlayPauseButton,
  PlayPauseButtonProps
} from "./videoPlayer/controls-player/play-pause-button"
import { LocalMuteIcon } from "./buttons-main-screen/local-mute-icon"
import {
  MuteButtonPlayer,
  MuteButtonPlayerProps
} from "./videoPlayer/controls-player/mute-button"
import { PlayerVolume } from "./videoPlayer/controls-player/volume"
import { LeaveMeetingButton, LeaveOverlay } from "./leave-overlay"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { brandingSelector } from "src/recoil/atoms/room"
import { EmotionalIcons } from "./buttons-main-screen/emotional-icons"
import { LeaveOverlayProps } from "./leave-overlay/leave-overlay.interface"
import { EmotionalIconsProps } from "./buttons-main-screen/buttons-main-screen.interface"
import { useWatchParty } from "src/hooks/useWatchParty"
import { PlayerVolumeProps } from "./videoPlayer/custom-player.interface"
import DragToResizeIcon from "./videoPlayer/controls-player/drag-to-resize-icon"
import { PlayerTime } from "./videoPlayer/controls-player/time"
import { playerPropsSelector, userSpecificiedMediaPlayerHeight } from "src/recoil/atoms/player"
import { useLocalUserType } from "src/hooks/useLocalUserType"
import useConfig from "src/hooks/useConfig"

function ActionBar() {
  const { watchPartyEnabled } = useConfig()
  const roomStyles = useRecoilValue(brandingSelector)
  const { isAudience } = useLocalUserType()
  const data = useContext(FloatingControlsCtx)
  const { watchPartyHostId } = useWatchParty()
  const video = document.getElementById(
    "watchpartyvideoplayer"
  ) as HTMLVideoElement

  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const { isOpenControlsMobile, isOpenControlDesktop, isEnabledToggleMobile } =
    useRecoilValue(playerPropsSelector)
  const setMediaHeight = useSetRecoilState(userSpecificiedMediaPlayerHeight)

  const onTouchStart: TouchEventHandler<HTMLDivElement> = e => {
    e.stopPropagation()
    // setPlayerProps({ isEnabledToggleMobile: false })
  }
  const onTouchEnd: TouchEventHandler<HTMLDivElement> = e => {
    e.stopPropagation()
    // setPlayerProps({ isEnabledToggleMobile: true })
  }
  // const onTouchMove: TouchEventHandler<HTMLDivElement> = e => {
    // e.stopPropagation()
    // const touchPosY = e.touches[0].clientY
    // setMediaHeight(Math.floor(touchPosY))
  // }

  
  return (
    <>
      <Box
        width="100%"
        height="50px"
        pos="absolute"
        bottom={0}
        bg="rgba(0, 0, 0, 0.6)"
        display="flex"
        alignItems="center"
        padding="10px 12px"
        gap="8px"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        // onTouchMove={onTouchMove}
      >
        {data.isVideo && (
          <PlayPauseButton
            paused={data.paused}
            handleTouchStart={data.handleTouchStart}
            playVideo={data.playVideo}
            handleOnTouchEndStop={data.handleOnTouchEndStop}
            pauseVideo={data.pauseVideo}
            handleOnTouchEndPlay={data.handleOnTouchEndPlay}
          />
        )}
        {data.isVideo && <LocalMuteIcon />}

        {watchPartyEnabled && watchPartyHostId === data.localUserId && data.isVideo && (
          <MuteButtonPlayer
            isMuted={data.isMuted}
            handleMute={data.handleMute}
            handleTouchStart={data.handleTouchStart}
            handleOnTouchEndMute={data.handleOnTouchEndMute}
          />
        )}

        {data.isVideo && (
          <PlayerVolume
            handleOnMouseMove={data.handleOnMouseMove}
            isControlVolume={data.isControlVolume}
            isVolumeScale={data.isVolumeScale}
            localUserId={data.localUserId}
            watchPartyHostId={data.watchPartyHostId}
            value={video?.volume ? video.volume * 100 : 0}
          />
        )}

        {data.showDragToResizeIcon ? (
          <>
            <DragToResizeIcon />
            <Box flexGrow={1}></Box>
            <Box pos="relative" right={data?.isPlayerFocused ? "2.2rem" : null}>
              <PlayerTime />
            </Box>
          </>
        ) : data.isVideo ? (
          <Box
            pos="relative"
            right={isAudience ? "2.2rem" : "unset"}
            ml="auto"
          >
            <PlayerTime />
          </Box>
        ) : (
          <></>
        )}
      </Box>
      {data.showBarPlayer && (
        <BarPlayer
          values={data.values}
          handleOnChange={data.handleOnChange}
          showThumb={data.showThumb}
        />
      )}
    </>
  )
}

type Progress = {
  values: number[]
  handleOnChange: (values: number[]) => void
  showThumb: boolean
}

type FloatingControlsCtxT = LeaveOverlayProps &
  Progress &
  EmotionalIconsProps &
  PlayPauseButtonProps &
  MuteButtonPlayerProps &
  Omit<PlayerVolumeProps, "value"> & {
    isOpen: boolean
    isMobile: boolean
    isTablet: boolean
    isVideo: boolean
    isOpenControlsMobile: boolean
    isOpenControlDesktop: boolean
    isPlayerFocused: boolean
    showBarPlayer: boolean
    showDragToResizeIcon: boolean
  }

export const FloatingControlsCtx = createContext<FloatingControlsCtxT>(null)

function FloatingControls() {
  const data = useContext(FloatingControlsCtx)
  const { voiceAndVideoEnabled, platformName } = useConfig()
  const { isAudience } = useLocalUserType()

  if(!data) return null

  // stop stacking context being created
  const styles = {...data.styling, zIndex: "auto !important"}

  return (
    <Box
      width="100%"
      height="100%"
      pos="absolute"
      top={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {voiceAndVideoEnabled && <LeaveOverlay
        handleTouchStart={data.handleTouchStart}
        handleTouchEnd={data.handleTouchEnd}
        handleClickVideo={data.handleClickVideo}
        styling={styles}
        isMobile={data.isMobile}
        isTouchDelay={data.isTouchDelay}
        isCustomPlayer={true}
        widthLeaveOverlay={data.widthLeaveOverlay}
        isMainMobileScreen={data.isMainMobileScreen}
        heightLeaveOverlay={
          data.isPlayerFocused && data.isVideo ? "calc(100% - 50px)" : "100%"
        }
        isVisible={data.isPlayerFocused && (data.isMobile || data.isTablet)}
      />}

      {data.isPlayerFocused && data.isVideo && <ActionBar />}
      <Flex
        position="absolute"
        right={data.isMobile && !data.isTablet ? "8px" : "12px"}
        id="emotional-icons"
        bottom="12px"
      >
        <EmotionalIcons
          isCustomPlayer={true}
          isMobile={true}
          offTouch={data.offTouch}
          isTransparent={true}
          isLandscape={false}
          localUserProperties={data.localUserProperties}
          isDesktop={data.isDesktop}
          changeSizeIconDependingHeightVideo={
            data.changeSizeIconDependingHeightVideo
          }
          showLeaveButton={isAudience && platformName !== "Utta"}
        />
      </Flex>
    </Box>
  )
}

export default FloatingControls
